import React from 'react';
import { getBadgeUrl } from '../../utils/badgeUrl';

// ── Palette utilities (local copies) ─────────────────────────────────────────

const hexToRgb = (hex) => {
    const h = String(hex || '').trim();
    if (!/^#[0-9A-Fa-f]{6}$/.test(h)) return { r: 255, g: 255, b: 255 };
    return { r: parseInt(h.slice(1, 3), 16), g: parseInt(h.slice(3, 5), 16), b: parseInt(h.slice(5, 7), 16) };
};

const clamp01 = (n) => Math.max(0, Math.min(1, Number(n)));

const rgbToHex = ({ r, g, b }) => {
    const to = (v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
    return `#${to(r)}${to(g)}${to(b)}`;
};

const mixToWhite = (hex, amountWhite) => {
    const t = clamp01(amountWhite);
    const c = hexToRgb(hex);
    return rgbToHex({ r: c.r + (255 - c.r) * t, g: c.g + (255 - c.g) * t, b: c.b + (255 - c.b) * t });
};

const srgbToLinear = (v) => { const x = v / 255; return x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4); };

const luminance = (hex) => {
    const { r, g, b } = hexToRgb(hex);
    return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
};

const contrastRatio = (bgHex, textHex) => {
    const L1 = luminance(bgHex); const L2 = luminance(textHex);
    return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);
};

const ensureReadableBgForDarkText = (bgHex, textHex = '#222222', minRatio = 4.5) => {
    let bg = String(bgHex || '#ffffff');
    for (let i = 0; i < 20; i++) { if (contrastRatio(bg, textHex) >= minRatio) return bg; bg = mixToWhite(bg, 0.08); }
    return bg;
};

const buildAchievementsPalette = (headerHex) => {
    const raw = String(headerHex || '').trim();
    if (!/^#[0-9A-Fa-f]{6}$/.test(raw)) return { rowEven: null, rowOdd: null };
    return {
        rowEven: ensureReadableBgForDarkText(mixToWhite(raw, 0.88)),
        rowOdd: ensureReadableBgForDarkText(mixToWhite(raw, 0.80)),
    };
};

const clearCachedData = (key) => {
    try { sessionStorage.removeItem(key); } catch {}
};

// ── FR1K sparkle config ───────────────────────────────────────────────────────

// Particles start at badge center (opacity 0, z-index above badge so they're never clipped).
// They fade in fast by 15% of travel, stay bright until 80%, then fade at the container edge.
// 32px cardinal / 23px-per-axis intercardinal (~32px radial) fills the 80px badge container.
const FR1K_PARTICLES = [
    { kf: 'fr1k-p0', delay: '0s',    dur: '1.8s', size: 7 },
    { kf: 'fr1k-p1', delay: '0.22s', dur: '2.0s', size: 9 },
    { kf: 'fr1k-p2', delay: '0.44s', dur: '1.7s', size: 6 },
    { kf: 'fr1k-p3', delay: '0.67s', dur: '2.1s', size: 8 },
    { kf: 'fr1k-p4', delay: '0.89s', dur: '1.9s', size: 7 },
    { kf: 'fr1k-p5', delay: '1.11s', dur: '2.0s', size: 9 },
    { kf: 'fr1k-p6', delay: '1.33s', dur: '1.8s', size: 6 },
    { kf: 'fr1k-p7', delay: '1.56s', dur: '2.1s', size: 8 },
];

const FR1K_CSS = `@keyframes fr1k-p0{0%{opacity:0;transform:translate(0,0) scale(.3)}15%{opacity:.95}80%{opacity:.85}100%{opacity:0;transform:translate(0px,-32px) scale(.5)}}@keyframes fr1k-p1{0%{opacity:0;transform:translate(0,0) scale(.3)}15%{opacity:.95}80%{opacity:.85}100%{opacity:0;transform:translate(23px,-23px) scale(.5)}}@keyframes fr1k-p2{0%{opacity:0;transform:translate(0,0) scale(.3)}15%{opacity:.95}80%{opacity:.85}100%{opacity:0;transform:translate(32px,0px) scale(.5)}}@keyframes fr1k-p3{0%{opacity:0;transform:translate(0,0) scale(.3)}15%{opacity:.95}80%{opacity:.85}100%{opacity:0;transform:translate(23px,23px) scale(.5)}}@keyframes fr1k-p4{0%{opacity:0;transform:translate(0,0) scale(.3)}15%{opacity:.95}80%{opacity:.85}100%{opacity:0;transform:translate(0px,32px) scale(.5)}}@keyframes fr1k-p5{0%{opacity:0;transform:translate(0,0) scale(.3)}15%{opacity:.95}80%{opacity:.85}100%{opacity:0;transform:translate(-23px,23px) scale(.5)}}@keyframes fr1k-p6{0%{opacity:0;transform:translate(0,0) scale(.3)}15%{opacity:.95}80%{opacity:.85}100%{opacity:0;transform:translate(-32px,0px) scale(.5)}}@keyframes fr1k-p7{0%{opacity:0;transform:translate(0,0) scale(.3)}15%{opacity:.95}80%{opacity:.85}100%{opacity:0;transform:translate(-23px,-23px) scale(.5)}}@keyframes fr1k-glow{0%,100%{opacity:.22;transform:scale(.85)}50%{opacity:.48;transform:scale(1.1)}}`;

// ── Component ─────────────────────────────────────────────────────────────────

const AchievementsBox = ({
    profileUsername,
    achievements,
    achievementsLoading,
    achievementStatsId,
    setAchievementStatsId,
    headerColour,
    timeAgo,
    loadProfileAchievements,
    isOwnProfile = false,
    pinnedBadgeID = null,
    savePinnedBadge = null,
}) => {
    const achievementsPalette = buildAchievementsPalette(headerColour);

    return (
        <div className="w-full max-w-full">
            <style>{`.habbo-scroll::-webkit-scrollbar{width:16px;height:16px}.habbo-scroll::-webkit-scrollbar-track{background:#cfcfcf;border:1px solid #000}.habbo-scroll::-webkit-scrollbar-thumb{background:#8f8f8f;border:1px solid #000}.habbo-scroll::-webkit-scrollbar-thumb:hover{background:#7a7a7a}${FR1K_CSS}`}</style>
            <div className="flex justify-end items-center px-2 py-1 border-b text-xs border-black/20 bg-black/5">
                Refresh Achievements
                <button
                    type="button"
                    onClick={async () => {
                        const key = `achievements_${profileUsername.toLowerCase()}`;
                        clearCachedData(key);
                        await loadProfileAchievements();
                    }}
                    className="w-5 h-5 ml-2 flex items-center justify-center border border-black/30 bg-black/10 hover:bg-black/20 transition-colors"
                    title="Refresh achievements"
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M20 12a8 8 0 1 1-2.34-5.66" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M20 4v6h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
            </div>
            {achievementsLoading ? (
                <div className="text-coal/70 p-3">Loading...</div>
            ) : achievements.length === 0 ? (
                <div className="text-coal/80 p-3">{profileUsername} has no achievements!</div>
            ) : (
                <div className="habbo-scroll w-full max-w-full h-[250px] overflow-y-auto">
                    <div className="flex flex-col">
                        {achievements.map((a, idx) => {
                            const isFr1k = a.badgeID === 'FR1K';
                            const rowBg = isFr1k ? null : (idx % 2 === 0 ? (achievementsPalette.rowEven || undefined) : (achievementsPalette.rowOdd || undefined));
                            const rowStyle = isFr1k
                                ? { backgroundColor: '#3d2828', boxShadow: 'inset 0 0 0 2px rgba(203,155,22,0.5)', borderColor: 'rgba(148,106,10,0.45)' }
                                : (rowBg ? { backgroundColor: rowBg } : undefined);
                            return (
                                <div
                                    key={`${a.badgeID}`}
                                    className={`w-full border-b${isFr1k ? '' : ` border-black${!rowBg ? (idx % 2 === 0 ? ' bg-black/5' : ' bg-black/10') : ''}`}`}
                                    style={rowStyle}
                                >
                                    <div className="flex gap-3 w-full">
                                        <div
                                            className={`relative w-[80px] flex items-center justify-center border-r px-2 py-2 group${isFr1k ? '' : ' bg-offwhite border-black/20'}`}
                                            style={isFr1k ? { backgroundColor: '#573333', borderRightColor: 'rgba(203,155,22,0.4)' } : undefined}
                                        >
                                            {isFr1k && (
                                                <>
                                                    {/* Ambient glow behind badge */}
                                                    <div
                                                        aria-hidden="true"
                                                        style={{
                                                            position: 'absolute',
                                                            width: '52px',
                                                            height: '52px',
                                                            left: '50%',
                                                            top: '50%',
                                                            transform: 'translate(-50%, -50%)',
                                                            borderRadius: '50%',
                                                            background: 'radial-gradient(circle, rgba(218,165,0,0.52) 0%, rgba(180,130,0,0.16) 45%, transparent 70%)',
                                                            pointerEvents: 'none',
                                                            animation: 'fr1k-glow 2.2s ease-in-out infinite',
                                                            willChange: 'transform, opacity',
                                                            zIndex: 0,
                                                        }}
                                                    />
                                                    {/* Sparkle particles — z-index 3 so they render above the badge image.
                                                        They start offset 8px from center (invisible) and fade in as they
                                                        emerge past the badge edge, creating the "radiating from behind" look. */}
                                                    {FR1K_PARTICLES.map(p => (
                                                        <span
                                                            key={p.kf}
                                                            aria-hidden="true"
                                                            style={{
                                                                position: 'absolute',
                                                                left: '50%',
                                                                top: '50%',
                                                                width: `${p.size}px`,
                                                                height: `${p.size}px`,
                                                                marginLeft: `-${p.size / 2}px`,
                                                                marginTop: `-${p.size / 2}px`,
                                                                borderRadius: '50%',
                                                                background: 'radial-gradient(circle at 38% 38%, #fffbb0 0%, #e8b800 55%, #c48000 100%)',
                                                                pointerEvents: 'none',
                                                                animation: `${p.kf} ${p.dur} ease-out ${p.delay} infinite backwards`,
                                                                willChange: 'transform, opacity',
                                                                zIndex: 3,
                                                            }}
                                                        />
                                                    ))}
                                                </>
                                            )}
                                            <img
                                                src={getBadgeUrl(a.badgeID, a.uploaded)}
                                                alt={a.badgeID}
                                                className="shrink-0"
                                                style={{
                                                    imageRendering: "pixelated",
                                                    maxWidth: "none",
                                                    maxHeight: "none",
                                                    ...(isFr1k ? {
                                                        position: 'relative',
                                                        zIndex: 1,
                                                        filter: 'drop-shadow(0 0 4px rgba(218,165,0,0.95)) drop-shadow(0 0 9px rgba(218,165,0,0.45))',
                                                    } : {}),
                                                }}
                                                loading="lazy"
                                            />
                                            <div className="hidden group-hover:block absolute left-[50px] top-0 ml-2 z-50">
                                                <div className="bg-offwhite border border-black p-2">
                                                    <div style={{ width: "calc(var(--bw) * 3)", height: "calc(var(--bh) * 3)" }}>
                                                        <img src={getBadgeUrl(a.badgeID, a.uploaded)} alt={`${a.badgeID} preview`} onLoad={(e) => { const img = e.currentTarget; img.parentElement.style.setProperty("--bw", `${img.naturalWidth || img.width}px`); img.parentElement.style.setProperty("--bh", `${img.naturalHeight || img.height}px`); }} style={{ imageRendering: "pixelated", transform: "scale(3)", transformOrigin: "top left", maxWidth: "none", maxHeight: "none" }} />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="min-w-0 flex-1 py-2 pr-2 relative">
                                            <div className="flex items-start justify-between gap-1">
                                                <div
                                                    className={`font-['Jersey_10'] text-xl leading-none truncate${isFr1k ? '' : ' text-coal'}`}
                                                    style={isFr1k ? { color: '#f0e4c4' } : undefined}
                                                    title={a.name}
                                                >{a.name}</div>
                                                <div className="flex items-center gap-1 shrink-0">
                                                    {isOwnProfile && savePinnedBadge && (
                                                        <button
                                                            type="button"
                                                            onClick={() => savePinnedBadge(a.badgeID, a.uploaded ?? false)}
                                                            style={{ fontSize: '9px' }}
                                                            className={[
                                                                'flex items-center gap-0.5 px-1.5 py-0.5 rounded border font-volterBold leading-none transition-colors mt-0.5 whitespace-nowrap',
                                                                pinnedBadgeID === a.badgeID
                                                                    ? 'bg-gold border-gold/70 text-coal'
                                                                    : isFr1k
                                                                        ? 'bg-white/10 border-gold/50 text-gold/90 hover:bg-white/15'
                                                                        : 'bg-coal/25 border-coal/20 text-offwhite hover:bg-coal/40',
                                                            ].join(' ')}
                                                            title={pinnedBadgeID === a.badgeID ? 'Unpin from bio' : 'Pin to bio'}
                                                        >
                                                            <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor" strokeLinecap="round" strokeLinejoin="round">
                                                                <line x1="12" y1="17" x2="12" y2="22" stroke="currentColor" strokeWidth="2.5"/>
                                                                <path d="M5 17H19V14L14 9V4H10V9L5 14V17Z"/>
                                                            </svg>
                                                            {pinnedBadgeID === a.badgeID ? 'Pinned' : 'Pin'}
                                                        </button>
                                                    )}
                                                    {a.earnedCount != null && (
                                                        <div className="relative">
                                                            <button
                                                                type="button"
                                                                onClick={() => setAchievementStatsId(achievementStatsId === a.badgeID ? null : a.badgeID)}
                                                                className={isFr1k
                                                                    ? 'w-4 h-4 rounded-full border border-gold/40 bg-white/10 text-gold/70 hover:text-gold hover:bg-white/20 flex items-center justify-center transition-colors mt-0.5'
                                                                    : 'w-4 h-4 rounded-full border border-black/30 bg-black/5 text-coal/60 hover:text-coal hover:bg-black/15 flex items-center justify-center transition-colors mt-0.5'
                                                                }
                                                                title="Achievement stats"
                                                            >
                                                                <span className="text-[11px] font-bold leading-none select-none">i</span>
                                                            </button>
                                                            {achievementStatsId === a.badgeID && (
                                                                <div className="absolute right-6 top-0 z-50 bg-offwhite border border-black/40 shadow-md rounded px-3 py-2 text-[11px] text-coal whitespace-nowrap">
                                                                    <div className="font-bold mb-0.5">Achievement Stats</div>
                                                                    <div>Claimed by <span className="font-bold">{a.earnedCount.toLocaleString()}</span> user{a.earnedCount !== 1 ? 's' : ''}!</div>
                                                                    <div className="text-coal/60">({a.earnedPct}% of all users)</div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div
                                                className={`mt-1 text-xs truncate${isFr1k ? '' : ' text-coal/90'}`}
                                                style={isFr1k ? { color: '#d0b990' } : undefined}
                                                title={a.description}
                                            >{a.description}</div>
                                            <div
                                                className="mt-2 border-t w-full"
                                                style={{ borderColor: isFr1k ? 'rgba(203,155,22,0.35)' : undefined }}
                                            />
                                            <div
                                                className={`mt-1 text-[11px] truncate${isFr1k ? '' : ' text-coal/70'}`}
                                                style={isFr1k ? { color: '#aa9060' } : undefined}
                                                title={`Gained ${timeAgo(a.dateAchieved)}`}
                                            >Gained {timeAgo(a.dateAchieved)}</div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AchievementsBox;
